/**
 * Pure helpers backing the Logs and Database operational panels.
 *
 * Kept framework-free so they can be unit-tested in the app's node test env
 * (mirrors `utilities/services-utilities.ts` and `utilities/activity.ts`).
 * These cover: shaping the read-only SQL console result into a renderable grid,
 * appending SSE log-stream deltas into a capped, deduped line buffer, resolving
 * which operational endpoint a panel action should hit, and presenting the
 * masked connection URL. No plaintext secret ever flows through here — the
 * backend only ever returns a `maskedUrl` (password redacted to `********`).
 *
 * @module
 */

import { sandboxErrorCopy } from './sandbox-error-copy.js'

/** The two environments the ops panels can target. */
export type OpsEnv = 'dev' | 'prod'

/**
 * Which dev-sandbox service's logs to read. The dev sandbox runs the backend
 * (`api`) and the Vite dev server (`app`) as separate processes; `'all'` tails
 * both. Ignored for prod (single combined log).
 */
export type LogService = 'all' | 'api' | 'app'

// ---------------------------------------------------------------------------
// Logs
// ---------------------------------------------------------------------------

/** Default cap on rendered log lines — keeps the DOM bounded for a live tail. */
export const DEFAULT_LOG_LINE_CAP = 2000

/** Shape of the `GET /projects/:id/logs` response. */
export interface LogsResponse {
  logs: string
  source: OpsEnv
  /** Which dev service was tailed (omitted by older servers → treat as 'all'). */
  service?: LogService
  containerId: string
  tail: number
}

/**
 * Reports whether a scrollable element is at (or within `threshold` px of) its
 * bottom. Used by the Logs panel to decide whether to keep auto-scrolling: at
 * the bottom we follow new output; scrolled up, we pause so the user can read.
 * @param el - The scrollable element (its scroll metrics).
 * @param threshold - Slack in px counted as "at bottom" (default 24).
 * @returns True when scrolled to (near) the bottom.
 */
export function isScrolledToBottom(
  el: Pick<HTMLElement, 'scrollTop' | 'scrollHeight' | 'clientHeight'>,
  threshold = 24,
): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold
}

/** One SSE event from `GET /projects/:id/logs/stream`. */
export type LogStreamEvent =
  | { type: 'logs'; source: OpsEnv; delta: string }
  | { type: 'error'; errorKey?: string }
  /**
   * Expected-empty stream (e.g. no prod deployment yet) — the backend emits
   * this ONCE and keeps the stream open. Render the quiet empty-state copy,
   * never an error banner.
   */
  | { type: 'empty'; errorKey?: string }
  /**
   * Routine server-side lifetime cap reached — the stream is about to close
   * normally. The client should re-subscribe silently (no error banner).
   */
  | { type: 'reconnect' }

/**
 * Backend `errorKey`s that mean "there's nothing running to show yet" — an
 * EXPECTED empty state (no dev sandbox, no prod deployment, the runtime is
 * asleep, container gone), NOT a failure. The Logs panel surfaces these as its
 * quiet empty-state copy rather than a red error banner.
 *
 * `projectOps.error.logsUnavailable` is deliberately absent: it means the server
 * could not REACH the provider, which is a failure and has to look like one. It
 * used to arrive as `containerNotFound` and read to the user as "your sandbox
 * isn't running" — indistinguishable from an idle project.
 */
export const EMPTY_STATE_ERROR_KEYS: ReadonlySet<string> = new Set([
  'projectOps.error.noSandbox',
  'projectOps.error.noProdInstance',
  'projectOps.error.runtimeAsleep',
  'projectOps.error.containerNotFound',
])

/**
 * Backend `errorKey`s the Database panel treats as an EXPECTED empty state ("no
 * database to inspect yet") rather than a failure. `noProdDatabase` is what a
 * "Prod" click returns for a project that was never deployed (the inspector no
 * longer provisions a prod DB on click — IDE7); the panel then shows a quiet
 * empty note instead of a red "Failed to load" banner.
 */
export const DATABASE_EMPTY_STATE_ERROR_KEYS: ReadonlySet<string> = new Set([
  'projectOps.error.noDatabase',
  'projectOps.error.noProdDatabase',
  // The sandbox is asleep/stopped: an expected state with a next step ("start
  // it"), not a failure. Its absence here is why the Database row was the one
  // storage row that rendered a red "Failed to list tables." for it while
  // cache/search showed the friendly note.
  'projectOps.error.sandboxNotRunning',
])

/**
 * The `errorKey` every project route returns when the caller's team role is
 * too low. It is a DELIBERATE denial, not a failure, so panels render it as a
 * read-only notice — the raw server string ("Access denied") in a red
 * monospace error box is what a viewer/editor used to get on first open, with
 * no explanation of what to do about it.
 */
export const INSUFFICIENT_ROLE_ERROR_KEY = 'project.error.insufficientRole'

/**
 * Reports whether an error is the insufficient-role denial.
 * @param error - The caught error.
 * @returns True when the caller's role is too low for this route.
 */
export function isInsufficientRoleError(error: unknown): boolean {
  return errorKeyOf(error) === INSUFFICIENT_ROLE_ERROR_KEY
}

/**
 * Reports whether an error is the vault's guest denial (`requiresSignup`), the
 * 403 a signed-out guest gets when saving credentials.
 * @param error - The caught error.
 * @returns True when saving requires a registered account.
 */
export function requiresSignup(error: unknown): boolean {
  const data = (error as { response?: { data?: { requiresSignup?: unknown } } } | null | undefined)
    ?.response?.data
  return data?.requiresSignup === true
}

/**
 * Pulls the backend `errorKey` off a thrown HTTP error. The API error body is
 * `{ error, errorKey }`, exposed on the rejected `HttpError` as `response.data`.
 * @param error - The caught error (any shape).
 * @returns The `errorKey` string, or null when absent.
 */
export function errorKeyOf(error: unknown): string | null {
  const data = (error as { response?: { data?: { errorKey?: unknown } } } | null | undefined)
    ?.response?.data
  return typeof data?.errorKey === 'string' ? data.errorKey : null
}

/**
 * Reports whether a tail/stream error is an EXPECTED empty state (no sandbox /
 * no prod deployment / no container) rather than a real failure. Used to
 * suppress the spurious error banner so the panel's empty-state copy shows
 * instead.
 * @param error - The caught error.
 * @returns True when the error's `errorKey` is one of {@link EMPTY_STATE_ERROR_KEYS}.
 */
export function isExpectedEmptyError(error: unknown): boolean {
  return logsEmptyStateKey(error) !== null
}

/**
 * The empty-state `errorKey` a tail failure carries, or null for a real failure.
 *
 * The panel keeps the KEY (not just the fact) so its copy can say which empty
 * state this is — "nothing deployed yet" and "the sandbox is asleep" are
 * different situations with different next steps, and one shared sentence for
 * both was a guess at whichever the user was in.
 *
 * @param error - The caught error.
 * @returns The `errorKey`, or null when this is not an expected empty state.
 */
export function logsEmptyStateKey(error: unknown): string | null {
  const key = errorKeyOf(error)
  return key !== null && EMPTY_STATE_ERROR_KEYS.has(key) ? key : null
}

/**
 * Maps a caught database-metadata error to its empty-state `errorKey` when it is
 * one of {@link DATABASE_EMPTY_STATE_ERROR_KEYS}, else null. The Database panel
 * uses this to show a quiet, env-specific empty note (e.g. "no production
 * database yet") instead of an error banner.
 * @param error - The caught error.
 * @returns The empty-state `errorKey`, or null for a real failure.
 */
export function databaseEmptyStateKey(error: unknown): string | null {
  const key = errorKeyOf(error)
  return key !== null && DATABASE_EMPTY_STATE_ERROR_KEYS.has(key) ? key : null
}

/**
 * Splits a log blob into individual lines, dropping a single trailing empty
 * line (the common `…\n` terminator) so we don't render a phantom blank row.
 * Interior blank lines are preserved.
 * @param blob - The raw log text.
 * @returns The lines, with at most the one trailing empty entry removed.
 */
export function splitLogLines(blob: string): string[] {
  if (!blob) return []
  const lines = blob.split('\n')
  if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop()
  return lines
}

/**
 * Appends a stream delta to an existing line buffer, capping the total to
 * `cap` lines (keeps the most-recent tail). Because the SSE stream's first
 * `delta` after a (re)connect can repeat content already shown — the backend
 * emits the full snapshot when it can't prove the new output is a strict
 * suffix of the last one — we drop any leading lines of the delta that exactly
 * re-state the current tail of the buffer (dedup), so reconnects don't
 * duplicate recent output.
 * @param current - The current line buffer.
 * @param delta - The new text from a `logs` SSE event.
 * @param cap - Max retained lines (defaults to {@link DEFAULT_LOG_LINE_CAP}).
 * @returns A new, capped line buffer.
 */
export function appendLogDelta(
  current: string[],
  delta: string,
  cap: number = DEFAULT_LOG_LINE_CAP,
): string[] {
  const incoming = splitLogLines(delta)
  if (incoming.length === 0) return current

  // Dedup: if the head of `incoming` overlaps the tail of `current`, drop the
  // overlapping prefix. Probe the largest possible overlap downward.
  const maxOverlap = Math.min(current.length, incoming.length)
  let overlap = 0
  for (let n = maxOverlap; n > 0; n--) {
    let matches = true
    for (let i = 0; i < n; i++) {
      if (current[current.length - n + i] !== incoming[i]) {
        matches = false
        break
      }
    }
    if (matches) {
      overlap = n
      break
    }
  }

  const fresh = overlap > 0 ? incoming.slice(overlap) : incoming
  if (fresh.length === 0) return current

  const next = current.concat(fresh)
  return next.length > cap ? next.slice(next.length - cap) : next
}

/** The detected severity of a single log line ('log' = neutral / unclassified). */
export type LogLevel = 'error' | 'warn' | 'info' | 'debug' | 'log'

/** The result of {@link classifyLogLine}. */
export interface LogLineClass {
  /** Detected severity. Defaults to 'log' (neutral) when no signal matches. */
  level: LogLevel
  /** The emitting dev service, when the line carries a leading `[api]`/`[app]` tag. */
  source?: 'api' | 'app'
}

/**
 * Ordered, case-insensitive level signals — most severe first, so a line that
 * matches several (e.g. a warning that also says "info") takes the highest. The
 * patterns are deliberately CONSERVATIVE: they anchor on recognised log-level
 * tokens (word-bounded so `info` doesn't fire on `information`, `ready` doesn't
 * fire on `already`), common status glyphs (`✖ ✗ ⚠ ✓ ✔`), and Node stack-trace
 * frames (`  at …`) — so ordinary prose stays the neutral 'log' level.
 */
const LOG_LEVEL_PATTERNS: ReadonlyArray<readonly [Exclude<LogLevel, 'log'>, RegExp]> = [
  ['error', /\b(?:error|errored|fatal|exception)\b|error[:!]|\berr\b|✖|✗|^\s*at\s+\S/i],
  ['warn', /\bwarn(?:ing)?s?\b|deprecat\w*|⚠/i],
  ['info', /\b(?:info|ready|listening)\b|✓|✔/i],
  ['debug', /\b(?:debug|trace|verbose)\b/i],
]

/**
 * Classifies one raw log line into a severity {@link LogLevel} (and, when tagged,
 * the emitting dev {@link LogLineClass.source}). Pure + DOM-free so the Logs panel
 * can color each line WITHOUT a per-line component (it maps the returned level to
 * a theme color token).
 *
 * Level detection is case-insensitive and conservative — it keys off recognised
 * log-level tokens (`error`/`ERR`/`fatal`/`exception`, `warn`/deprecation,
 * `info`/`ready`/`listening`, `debug`/`trace`/`verbose`), status glyphs
 * (`✖ ✗ ⚠ ✓ ✔`), and Node stack-trace frames (`  at …`). Severity wins
 * highest-first; anything with no signal stays the neutral `'log'` level so a
 * plain line isn't spuriously tinted.
 *
 * Source is read from a leading `[api]`/`[app]` tag (the dev sandbox prefixes its
 * two processes this way — see the logs-panel tests). Absent → `source` omitted.
 * @param line - One raw log line.
 * @returns The detected level and (optional) source.
 */
export function classifyLogLine(line: string): LogLineClass {
  const result: LogLineClass = { level: 'log' }

  const sourceMatch = /^\s*\[(api|app)\]/i.exec(line)
  if (sourceMatch) result.source = sourceMatch[1].toLowerCase() as 'api' | 'app'

  for (const [level, pattern] of LOG_LEVEL_PATTERNS) {
    if (pattern.test(line)) {
      result.level = level
      break
    }
  }
  return result
}

// ---------------------------------------------------------------------------
// Database connection metadata
// ---------------------------------------------------------------------------

/** Shape of the `GET /projects/:id/database` response (password always masked). */
export interface DatabaseInfo {
  env: OpsEnv
  host: string
  port: number
  database: string
  user: string
  /** Connection URL with the password redacted to `********`. */
  maskedUrl: string
  /**
   * WHO can dial this address: `sandbox` (the dev DB's `127.0.0.1` inside the
   * project's own sandbox — on a laptop that points at the laptop) or
   * `deployment` (the platform's private cluster, reachable from the deployed
   * app). Both are correct for the APP and dialable by nobody else, so the
   * panel has to say so rather than caption a copyable string "hosted and
   * ready". Optional: an older server omits it.
   */
  reachableFrom?: 'sandbox' | 'deployment'
}

/**
 * Returns the masked URL exactly as the backend supplied it, guarding against a
 * missing field (renders an em-dash placeholder rather than `undefined`). The
 * backend NEVER returns a plaintext password here, so this is always safe to
 * display and to copy.
 * @param info - The database info (or null while loading).
 * @returns The masked URL string, or `'—'` when unavailable.
 */
export function displayMaskedUrl(info: DatabaseInfo | null): string {
  const url = info?.maskedUrl?.trim()
  return url ? url : '—'
}

// ---------------------------------------------------------------------------
// Database browser (tables)
// ---------------------------------------------------------------------------

/** One column of an introspected table. */
export interface TableColumn {
  name: string
  dataType: string
  nullable: boolean
}

/** One public table with its ordered columns. */
export interface TableInfo {
  name: string
  columns: TableColumn[]
}

/** Shape of the `GET /projects/:id/database/tables` response. */
export interface TablesResponse {
  tables: TableInfo[]
  count: number
}

/** Default row cap for a one-click table preview (`SELECT * … LIMIT n`). */
export const TABLE_PREVIEW_LIMIT = 100

/**
 * Hard server-side row cap for ANY console query — mirrors the backend's
 * `MAX_QUERY_ROWS` (`ProjectOps/service.ts`). A result is flagged `truncated`
 * only when the underlying query returned MORE than this many rows, so this is
 * the limit the "server cap reached" indicator reports — distinct from the far
 * smaller {@link TABLE_PREVIEW_LIMIT} a one-click preview applies.
 */
export const QUERY_ROW_CAP = 1000

/**
 * Builds a column-name → data-type lookup from an introspected table, used to
 * annotate result-grid headers with type hints. Returns `{}` when no table is
 * given (e.g. an ad-hoc query whose columns aren't tied to a known table).
 * @param table - The introspected table, or null/undefined.
 * @returns A map of column name to its SQL data type.
 */
export function columnTypeMap(table: TableInfo | null | undefined): Record<string, string> {
  const map: Record<string, string> = {}
  if (!table) return map
  for (const col of table.columns) map[col.name] = col.dataType
  return map
}

/**
 * Builds the read-only `SELECT * … LIMIT 100` preview query for a table.
 * Double-quotes the identifier (and escapes embedded quotes) so table names
 * with mixed case or reserved words work. The result is still executed under
 * the backend's transaction-level read-only guard.
 * @param tableName - The public table name to preview.
 * @param limit - Row limit (defaults to {@link TABLE_PREVIEW_LIMIT}).
 * @returns The SQL string.
 */
export function tablePreviewQuery(tableName: string, limit = TABLE_PREVIEW_LIMIT): string {
  const quoted = `"${tableName.replace(/"/g, '""')}"`
  return `SELECT * FROM ${quoted} LIMIT ${limit}`
}

// ---------------------------------------------------------------------------
// Read-only SQL console — result-grid shaping
// ---------------------------------------------------------------------------

/** Shape of the `POST /projects/:id/database/query` response. */
export interface QueryResult {
  columns: string[]
  rows: Record<string, unknown>[]
  rowCount: number
  truncated: boolean
  /**
   * WHICH cap truncated the result: `rows` = the {@link QUERY_ROW_CAP} row
   * cap; `bytes` = the in-sandbox path's size cap. Absent on older servers
   * (and when nothing was truncated) — {@link rowCapNotice} reads a missing
   * value as the row cap, which is the only cap the cluster path has.
   */
  truncatedBy?: 'rows' | 'bytes'
}

/** A result shaped for grid rendering: header columns + string-formatted cells. */
export interface ResultGrid {
  columns: string[]
  /** Each row is an array of cell strings, aligned to `columns`. */
  rows: string[][]
  rowCount: number
  truncated: boolean
  /** Which cap truncated the result — see {@link QueryResult.truncatedBy}. */
  truncatedBy?: 'rows' | 'bytes'
  /** True when the query returned columns but zero rows (e.g. an empty table). */
  empty: boolean
}

/**
 * Formats a single SQL cell value for text display in the results grid.
 * `null`/`undefined` → `'NULL'`; objects/arrays → compact JSON; everything else
 * → `String(value)`.
 * @param value - The raw cell value from the query result.
 * @returns A display string.
 */
export function formatCell(value: unknown): string {
  if (value === null || value === undefined) return 'NULL'
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch (_error) {
      // JSON.stringify can throw for circular references; String() is a safe fallback
      return String(value)
    }
  }
  return String(value)
}

/** A cell value expanded for the click-to-expand detail popover. */
export interface ExpandedCell {
  /** The full value, pretty-printed when it parses as JSON, else unchanged. */
  text: string
  /** True when the value was recognised + reflowed as JSON (drives the label). */
  isJson: boolean
}

/**
 * Expands a grid cell string for the detail popover. The grid stores objects as
 * COMPACT single-line JSON (see {@link formatCell}); for the expand view we
 * re-indent valid JSON to multi-line so a wide/nested value is readable, and
 * leave plain scalars untouched. Pure so it can be unit-tested without a DOM.
 * @param cell - The already-formatted cell string from a {@link ResultGrid} row.
 * @returns The expanded text plus whether it was reflowed as JSON.
 */
export function prettyCellValue(cell: string): ExpandedCell {
  const trimmed = cell.trim()
  // Only attempt a re-indent for things that actually look like JSON objects or
  // arrays — never coerce a bare scalar like `42` or `true` into "JSON".
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return { text: JSON.stringify(JSON.parse(trimmed), null, 2), isJson: true }
    } catch (_error) {
      // Not valid JSON after all (e.g. a string that merely starts with `{`);
      // fall through and show it verbatim.
    }
  }
  return { text: cell, isJson: false }
}

/**
 * Shapes a raw {@link QueryResult} into a {@link ResultGrid} ready to render —
 * stable column order, each row projected to a string-cell array aligned to the
 * columns (missing keys render as `NULL`), plus convenience flags.
 * @param result - The raw query result (or null when no query has run yet).
 * @returns The shaped grid, or null when there is nothing to render.
 */
export function shapeResultGrid(result: QueryResult | null): ResultGrid | null {
  if (!result) return null
  const columns = result.columns ?? []
  const rawRows = result.rows ?? []
  const rows = rawRows.map((row) => columns.map((col) => formatCell(row[col])))
  return {
    columns,
    rows,
    rowCount: result.rowCount ?? rows.length,
    truncated: !!result.truncated,
    ...(result.truncatedBy ? { truncatedBy: result.truncatedBy } : {}),
    empty: columns.length > 0 && rows.length === 0,
  }
}

/** A row-cap notice for the results grid (or null when no cap was reached). */
export interface RowCapNotice {
  /**
   * `serverCap` — the query genuinely returned more than the hard server cap
   * ({@link QUERY_ROW_CAP}); `previewCap` — a one-click table preview filled its
   * {@link TABLE_PREVIEW_LIMIT} (there may be more rows in the table);
   * `sizeCap` — the result was cut by its SIZE, not its row count, so no row
   * number is worth quoting as a cap.
   */
  kind: 'serverCap' | 'previewCap' | 'sizeCap'
  /** Rows actually shown. */
  rowCount: number
  /** The cap that was reached (0 for `sizeCap` — the cap is a byte budget). */
  limit: number
}

/**
 * Decide whether to tell the user a row cap was reached.
 *
 *  - A hand-typed query is authoritative: the backend sets `truncated` when it
 *    sliced the result at {@link QUERY_ROW_CAP}, so we report the server cap.
 *  - A one-click table preview runs `SELECT * … LIMIT {@link TABLE_PREVIEW_LIMIT}`.
 *    The server-side `truncated` flag can NEVER reflect that — the server caps at
 *    the far-higher {@link QUERY_ROW_CAP}, so a 100-row preview is never
 *    "truncated". This was the IDE7 gap: the limit indicator could never fire for
 *    the common table-click path. We instead infer the preview cap was reached
 *    when exactly {@link TABLE_PREVIEW_LIMIT} rows came back.
 *
 *  - A result cut by SIZE rather than row count reports `sizeCap`. Reporting
 *    it as the server cap produced a notice that contradicted itself —
 *    "showing the first 120 rows (server cap of 1000)" — naming a cap the
 *    query never came near.
 *  - A truncated result with ZERO rows (one row larger than the whole size
 *    budget) still gets a notice. It used to get none, so the panel rendered
 *    "Query returned no rows." for a query that returned data.
 *
 * @param grid - The shaped result grid (or null when nothing has run).
 * @param isPreview - Whether `grid` came from a one-click table preview.
 * @returns The notice to show, or null when no cap was reached.
 */
export function rowCapNotice(grid: ResultGrid | null, isPreview: boolean): RowCapNotice | null {
  if (!grid) return null
  if (grid.truncated) {
    return grid.truncatedBy === 'bytes'
      ? { kind: 'sizeCap', rowCount: grid.rowCount, limit: 0 }
      : { kind: 'serverCap', rowCount: grid.rowCount, limit: QUERY_ROW_CAP }
  }
  if (grid.empty) return null
  if (isPreview && grid.rowCount >= TABLE_PREVIEW_LIMIT) {
    return { kind: 'previewCap', rowCount: grid.rowCount, limit: TABLE_PREVIEW_LIMIT }
  }
  return null
}

// ---------------------------------------------------------------------------
// Endpoint resolvers — which endpoint each panel action calls.
// Centralized so the wiring is unit-testable and endpoint strings aren't
// duplicated across the panels (mirrors services-utilities' action resolvers).
// ---------------------------------------------------------------------------

/** Resolves `GET /projects/:id/logs?source=…&service=…&tail=N`. */
export function logsRequest(
  projectId: string,
  source: OpsEnv,
  tail: number,
  service: LogService = 'all',
): { path: string; params: { source: OpsEnv; service: LogService; tail: number } } {
  return { path: `/projects/${projectId}/logs`, params: { source, service, tail } }
}

/** Resolves the SSE stream URL `GET /projects/:id/logs/stream?source=…&service=…&tail=N`. */
export function logsStreamPath(
  projectId: string,
  source: OpsEnv,
  tail: number,
  service: LogService = 'all',
): string {
  return `/projects/${projectId}/logs/stream?source=${source}&service=${service}&tail=${tail}`
}

/** Resolves the SSE stream URL `GET /projects/:id/preview/health/stream`. */
export function previewHealthStreamPath(projectId: string): string {
  return `/projects/${projectId}/preview/health/stream`
}

/**
 * One SSE event from `GET /projects/:id/preview/health/stream`.
 *
 * `status` is `'down'` when the Vite dev server crashed/erroring, `'ok'` when it
 * loads, `'unknown'` when the sandbox isn't running (paused). `autoFix` mirrors the
 * project's `/autofix` setting and is the gate for the IDE's autonomous fix. `message`
 * (down only) is the secret-redacted Vite error. `reconnect` is the routine
 * lifetime-cap sentinel — re-subscribe silently.
 */
export type PreviewHealthStreamEvent =
  | {
      type: 'preview_health'
      status: 'ok' | 'down' | 'unknown'
      httpStatus?: number
      message?: string
      autoFix: boolean
    }
  | { type: 'reconnect' }

/** Resolves `GET /projects/:id/database?env=…`. */
export function databaseRequest(
  projectId: string,
  env: OpsEnv,
): { path: string; params: { env: OpsEnv } } {
  return { path: `/projects/${projectId}/database`, params: { env } }
}

/** Resolves `GET /projects/:id/database/tables?env=…`. */
export function tablesRequest(
  projectId: string,
  env: OpsEnv,
): { path: string; params: { env: OpsEnv } } {
  return { path: `/projects/${projectId}/database/tables`, params: { env } }
}

/** Resolves `POST /projects/:id/database/query { sql, env }`. */
export function queryRequest(
  projectId: string,
  sql: string,
  env: OpsEnv,
): { path: string; body: { sql: string; env: OpsEnv } } {
  return { path: `/projects/${projectId}/database/query`, body: { sql, env } }
}

// ---------------------------------------------------------------------------
// Storage probes — the cache / search / object-storage explorers
// (`POST /projects/:id/storage/probe`, whitelisted server-side)
// ---------------------------------------------------------------------------

/** The whitelisted storage-probe kinds the backend accepts. */
export type StorageProbeKind = 'cache' | 'search' | 'objects'

/**
 * A failed probe. Every flag distinguishes a situation the panel must say
 * something DIFFERENT about — one shared sentence for all of them is how a
 * dead Meilisearch sidecar got explained to users as a healthy Postgres setup.
 *
 *  - `notConfigured` — the app has no such storage (no `REDIS_URL`, no search
 *    server). Expected; a quiet hint, never a red error.
 *  - `serverDown` — the app HAS this provider and its server is not answering.
 *    Actionable, and the opposite advice from `notConfigured`.
 *  - `unsupported` — the provider cannot do this action at all (memcached
 *    cannot enumerate keys). A capability statement, not a failure.
 *  - `sandboxNotRunning` — the 409 for no running sandbox to probe.
 *  - `forbidden` — the caller's team role is too low (a deliberate denial).
 *  - `provider` — which provider the probe actually found, so the panel labels
 *    the row from reality instead of assuming one provider per storage type.
 *
 * `error` carries the server-provided message when one exists (may be empty
 * for transport failures — the panel substitutes its own i18n copy then).
 */
export interface StorageProbeFailure {
  ok: false
  error: string
  notConfigured?: boolean
  serverDown?: boolean
  unsupported?: boolean
  sandboxNotRunning?: boolean
  forbidden?: boolean
  provider?: string
}

/** The success/failure union `POST /projects/:id/storage/probe` resolves to. */
export type StorageProbeResult<T = unknown> = { ok: true; data: T } | StorageProbeFailure

/** `cache:stats` payload — key count plus a compact server/memory slice. */
export interface CacheStatsData {
  /**
   * Which cache server answered ('redis' | 'memcached'). The row's label and
   * explainer come from this, not from an assumption that every cache is
   * Redis — a memcached project used to be told "Redis runs inside your app's
   * container" and then that no cache was running.
   */
  provider?: string
  dbsize: number
  redis_version?: string
  /** Server version for providers that are not Redis (e.g. memcached). */
  server_version?: string
  uptime_in_seconds?: string
  used_memory_human?: string
  maxmemory_human?: string
}

/** `cache:keys` payload — SCAN-matched keys, capped at 100 server-side. */
export interface CacheKeysData {
  keys: string[]
  truncated: boolean
}

/** `cache:get` payload — type-aware, size-capped read (`type: 'none'` = missing). */
export interface CacheGetData {
  key: string
  type: string
  ttl?: number
  value?: string
  size?: number | null
  truncated?: boolean
}

/** One search index (Meilisearch has `primaryKey`, Typesense has `documents`). */
export interface SearchIndexInfo {
  uid: string
  primaryKey?: string | null
  documents?: number
}

/** `search:indexes` payload. */
export interface SearchIndexesData {
  provider: string
  indexes: SearchIndexInfo[]
}

/** `search:query` payload — capped at 10 hits server-side. */
export interface SearchQueryData {
  provider: string
  hits: Record<string, unknown>[]
  total: number
}

/** One listed S3 object. */
export interface S3ObjectInfo {
  key: string
  size: number
  lastModified: string | null
}

/** `objects:list` payload — capped at 100 objects server-side. */
export interface ObjectsListData {
  bucket: string
  objects: S3ObjectInfo[]
  truncated: boolean
}

/**
 * Pulls the backend's human-readable `error` message off a thrown HTTP error
 * (the API error body is `{ error, errorKey }`, exposed on the rejected
 * `HttpError` as `response.data`).
 * @param error - The caught error (any shape).
 * @returns The server `error` string, or null when absent.
 */
export function serverErrorMessage(error: unknown): string | null {
  const data = (
    error as
      | { response?: { data?: { error?: unknown; errorKey?: unknown; messageKey?: unknown } } }
      | null
      | undefined
  )?.response?.data
  // A `messageKey` (a lost project Start restores) outranks its `errorKey`.
  const copy =
    sandboxErrorCopy(typeof data?.messageKey === 'string' ? data.messageKey : null) ??
    sandboxErrorCopy(typeof data?.errorKey === 'string' ? data.errorKey : null)
  if (copy) return copy
  return typeof data?.error === 'string' ? data.error : null
}

/**
 * Resolves `POST /projects/:id/storage/probe { kind, action, args? }`.
 * `args` is omitted from the body when empty (the backend treats both the
 * same; this keeps request logs clean).
 * @param projectId - The project to probe.
 * @param kind - The probe kind (cache / search / objects).
 * @param action - The whitelisted action for that kind.
 * @param args - Optional string arguments (pattern / key / index / query / prefix).
 * @returns The request path + body.
 */
export function probeRequest(
  projectId: string,
  kind: StorageProbeKind,
  action: string,
  args?: Record<string, string>,
): {
  path: string
  body: { kind: StorageProbeKind; action: string; args?: Record<string, string> }
} {
  return {
    path: `/projects/${projectId}/storage/probe`,
    body: args && Object.keys(args).length > 0 ? { kind, action, args } : { kind, action },
  }
}

/** The one HttpClient method {@link probeStorage} needs (keeps this module framework-free). */
export interface ProbeHttp {
  post<T = unknown>(url: string, data?: unknown): Promise<{ data: T }>
}

/**
 * Runs one whitelisted storage probe and normalizes EVERY outcome into the
 * {@link StorageProbeResult} union, so panels never need a try/catch:
 *
 *  - HTTP 200 `{ ok, ... }` bodies pass through unchanged (including the
 *    friendly `notConfigured` / `serverDown` / `unsupported` failures and the
 *    `provider` the probe detected).
 *  - The 409 "sandbox not running" HTTP error becomes a failure with
 *    `sandboxNotRunning: true`.
 *  - The 403 insufficient-role denial becomes `forbidden: true` — a deliberate
 *    denial the panel explains, not a red box with the raw word "Access
 *    denied" in it.
 *  - Any other HTTP/transport error becomes `{ ok: false, error }` carrying
 *    the server message when one exists (empty string otherwise — callers
 *    substitute their own i18n fallback copy).
 *  - A malformed 200 body (no boolean `ok`) is treated as a failure too.
 *
 * @param http - The HTTP client (only `post` is used).
 * @param projectId - The project to probe.
 * @param kind - The probe kind (cache / search / objects).
 * @param action - The whitelisted action for that kind.
 * @param args - Optional string arguments (pattern / key / index / query / prefix).
 * @returns The normalized probe result.
 */
export async function probeStorage<T = unknown>(
  http: ProbeHttp,
  projectId: string,
  kind: StorageProbeKind,
  action: string,
  args?: Record<string, string>,
): Promise<StorageProbeResult<T>> {
  const { path, body } = probeRequest(projectId, kind, action, args)
  try {
    const res = await http.post<StorageProbeResult<T>>(path, body)
    const result = res.data
    if (
      result &&
      typeof result === 'object' &&
      typeof (result as { ok?: unknown }).ok === 'boolean'
    ) {
      return result
    }
    return { ok: false, error: '' }
  } catch (error) {
    if (errorKeyOf(error) === 'projectOps.error.sandboxNotRunning') {
      return { ok: false, error: serverErrorMessage(error) ?? '', sandboxNotRunning: true }
    }
    if (isInsufficientRoleError(error)) {
      return { ok: false, error: serverErrorMessage(error) ?? '', forbidden: true }
    }
    return { ok: false, error: serverErrorMessage(error) ?? '' }
  }
}

// ---------------------------------------------------------------------------
// Dev files browser (object-storage row) — pure presentation helpers
// ---------------------------------------------------------------------------

/**
 * Extensions the files browser never text-previews — obviously binary formats,
 * skipped WITHOUT a round trip. This is an optimization, not the guard: the
 * server sniffs the actual content and answers `{ binary: true }` for anything
 * this list misses, which is most of them. The uploads directory in particular
 * stores every file under an extensionless UUID (the filesystem uploads bond
 * writes `uploads/<uuid>`), so an extension list alone could not catch even
 * the PNGs and PDFs it was written for.
 */
export const BINARY_PREVIEW_EXTENSIONS: ReadonlySet<string> = new Set([
  '7z',
  'avi',
  'bin',
  'br',
  'bz2',
  'class',
  'dll',
  'dmg',
  'doc',
  'docx',
  'dylib',
  'eot',
  'exe',
  'flac',
  'gif',
  'gz',
  'heic',
  'ico',
  'jar',
  'jpeg',
  'jpg',
  'mov',
  'mp3',
  'mp4',
  'odt',
  'ogg',
  'otf',
  'parquet',
  'pdf',
  'png',
  'ppt',
  'pptx',
  'psd',
  'rar',
  'so',
  'sqlite',
  'tar',
  'tgz',
  'ttf',
  'wasm',
  'wav',
  'webm',
  'webp',
  'woff',
  'woff2',
  'xls',
  'xlsx',
  'zip',
  'zst',
])

/**
 * Reports whether a file name/path has an obviously-binary extension (see
 * {@link BINARY_PREVIEW_EXTENSIONS}). Case-insensitive; extensionless names
 * are NOT assumed binary — the server decides those by sniffing the content.
 * @param path - The file name or path.
 * @returns True when the file can be skipped without a fetch.
 */
export function isBinaryPreviewPath(path: string): boolean {
  const base = path.slice(path.lastIndexOf('/') + 1)
  const dot = base.lastIndexOf('.')
  if (dot <= 0) return false
  return BINARY_PREVIEW_EXTENSIONS.has(base.slice(dot + 1).toLowerCase())
}

/**
 * Byte size above which the files browser refuses to fetch a text preview.
 *
 * The preview renders at most {@link FILE_PREVIEW_CHAR_LIMIT} characters, so
 * pulling a multi-megabyte file across the network to slice 4 KB off the front
 * is pure waste — the server's own 5 MB read cap is a memory guard, not a
 * sensible preview budget.
 */
export const FILE_PREVIEW_MAX_BYTES = 512 * 1024

/** Character cap for the bounded file-preview block. */
export const FILE_PREVIEW_CHAR_LIMIT = 4000

/**
 * Formats a byte count for compact display (`0 B`, `512 B`, `2.0 KB`, `1.5 MB`).
 * Negative/non-finite input renders as an em-dash placeholder.
 * @param bytes - The byte count.
 * @returns The formatted size string.
 */
export function formatByteSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB'] as const
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }
  return unit === 0 ? `${value} B` : `${value.toFixed(1)} ${units[unit]}`
}
